import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { CancelOutlined, MoreHoriz } from "@mui/icons-material";
import {
  apiCallLoucaCoinList,
  apiCallLoucaCoinSettings,
  apiCallStatusUpdate,
} from "../../../Core/Apicall";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../Redux/Store";
import { clearShowMenu } from "../../../Redux/Reducers/GlobalVar";
import DeletePopup from "../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../Common/CommonStatus/CommonStatus";
import CommonShimmer from "../../Common/CommonShimmer/CommonShimmer";
import { toast } from "react-toastify";

export const LOUCA_COIN_TYPE = {
  CHECK_IN: "Check-In",
  REFER_FRIEND: "Refer-Friend",
  CREATE_REEL: "Reels",
  UPLOAD_MEDIA: "Upload-Media",
  REVIEW: "Review",
  STAR_RATING: "Star-Rating",
  QUICK_SURVEY: "Quick-Survey",
};

interface CoinType {
  _id?: string;
  type: string;
  coins: any;
  isActive: boolean;
  isDelete: boolean;
}

function LoucaCoins() {
  const [data, setData] = useState<CoinType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const showMenu: any = useSelector(
    (state: RootState) => state.global.showMenu
  );
  const userdata: any = useSelector((state: RootState) => state.global.token);

  const [isCreateNew, setIsCreateNew] = useState<boolean>(!!showMenu);
  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  const [formData, setFormData] = useState<any>({
    _id: "",
    coinId: "",
    type: "",
    coins: 0,
    isActive: true,
    isDelete: false,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<CoinType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCoin = async () => {
      setLoading(true);
      try {
        const response = await apiCallLoucaCoinList({});
        if (response?.data) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (err: any) {
      } finally {
        setLoading(false);
      }
    };
    fetchCoin();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.type) {
      toast.error("Please select Louca Coin Type");
      return;
    }

    try {
      if (formData.coinId) {
        const data = {
          adminId: userdata?.data?.userId,
          coinId: formData.coinId,
          type: formData.type,
          coins: formData.coins,
        };

        await apiCallLoucaCoinSettings(data);
        toast.success("Coin update successfully!");
      } else {
        await apiCallLoucaCoinSettings(formData);
        toast.success("Coin saved successfully!");
      }
      setIsCreateNew(false);
      setFormData({
        coinId: "",
        type: "",
        coins: 0,
        isActive: true,
        isDelete: false,
      });

      const response = await apiCallLoucaCoinList({});
      setData(response?.data || []);
    } catch (err) {
      toast.error("Failed to save coin");
    }
  };

  const handleStatusUpdate = async (item: any) => {
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        typeId: item._id,
        type: "louca_coin",
        isActive: !item.isActive,
      });
      setData((prev) =>
        prev.map((a) =>
          a._id === item._id ? { ...a, isActive: !item.isActive } : a
        )
      );
      setStatusDialogOpen(false);
    } catch (err) {}
  };

  const handleDelete = async () => {
    if (!menuVibe) return;
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        type: "louca_coin",
        typeId: menuVibe._id,
        isDelete: true,
      });
      setData((prev) => prev.filter((item) => item._id !== menuVibe._id));
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false);
      setMenuVibe(null);
    }
  };

  // ------------------- Menu Handlers -------------------
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    setAnchorEl(event.currentTarget);
    setMenuVibe(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            ...menuVibe,
            coinId: menuVibe._id,
          });
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    },
  ];

  return (
    <Box style={{ paddingBottom: "20px" }}>
      <Box className="d-flex input align-items-center justify-content-end">
        <Button
          className="btn-black p-2 px-3"
          onClick={() => {
            setFormData({
              _id: "",
              type: "",
              coins: 0,
              isActive: true,
              isDelete: false,
            });
            setIsCreateNew(true);
          }}
        >
          + Create New
        </Button>
      </Box>

      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <TableContainer className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Coin Type</TableCell>
                <TableCell>Coins</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, index: any) => (
                <TableRow hover key={index}>
                  <TableCell>{item.type}</TableCell>
                  <TableCell>{item.coins}</TableCell>

                  <TableCell>
                    <Button
                      className={item?.isActive ? "btn-success" : "btn-danger"}
                      onClick={() => setStatusDialogOpen(index)}
                    >
                      {item?.isActive ? "Active" : "Inactive"}
                    </Button>
                    <CommonStatus
                      open={statusDialogOpen === index}
                      title={item?.type}
                      message={`Are you sure you want to ${
                        item?.isActive ? "deactivate" : "activate"
                      } this venue?`}
                      confirmText={item?.isActive ? "Deactivate" : "Activate"}
                      confirmColor={item?.isActive ? "warning" : "success"}
                      onConfirm={() => handleStatusUpdate(item)}
                      onClose={() => setStatusDialogOpen(false)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                      <MoreHoriz />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loading && (
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>
          )}
        </TableContainer>
      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this louca coins?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false);
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: {
            width: "35%",
            borderRadius: "16px 0 0 16px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          className="avatars-form"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          {/* ---------- Header ---------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData._id ? "Edit Louca Coin" : "Create Louca Coin"}
            </Typography>
            <IconButton
              onClick={() => {
                setIsCreateNew(false);
                dispatch(clearShowMenu());
              }}
            >
              <CancelOutlined />
            </IconButton>
          </Box>

          {/* ---------- Form ---------- */}
          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Louca Coin Type
                </Typography>
                <Select
                  name="type"
                  disabled={!!formData.coinId}
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      type: e.target.value,
                    }))
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  {Object.values(LOUCA_COIN_TYPE).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Coins
                </Typography>
                <TextField
                  name="coins"
                  value={formData.coins}
                  onChange={handleChange}
                  placeholder="Enter Coins"
                  fullWidth
                />
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default LoucaCoins;
